import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { apiFetch, parseJsonSafe } from '../utils/api';

const PAGE_SIZE = 20;

const AudienceContactsPage = () => {
  const token = localStorage.getItem('token');
  const { id } = useParams();
  const location = useLocation();
  const [audience, setAudience] = useState(location.state?.audience || null);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusUpdating, setStatusUpdating] = useState('');
  const [syncingElastic, setSyncingElastic] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [syncingElasticEvents, setSyncingElasticEvents] = useState(false);
  const [editAudienceModal, setEditAudienceModal] = useState({ open: false, name: '', description: '' });
  const [editModal, setEditModal] = useState({ open: false, contact: null, name: '', phone: '' });
  const [importModal, setImportModal] = useState({
    open: false,
    uploading: false,
    importing: false,
    error: '',
    fileId: '',
    fileName: '',
    columns: [],
    preview: [],
    totalRows: 0,
    mapping: { email: '', name: '', phone: '' }
  });

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    window.location.href = '/login';
  };

  const isManualAudience = () => {
    const origin = Array.isArray(audience?.filters?.originTypes) ? audience.filters.originTypes : [];
    return origin.length > 0 && origin.every((t) => t === 'manual');
  };

  const guessColumn = (columns, hints) => {
    const lower = columns.map((c) => c.toLowerCase());
    for (const hint of hints) {
      const idx = lower.findIndex((c) => c.includes(hint));
      if (idx >= 0) return columns[idx];
    }
    return '';
  };

  const fetchAudience = async () => {
    try {
      const res = await apiFetch(`/api/email-marketing/audiences/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo cargar la audiencia');
      setAudience(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchContacts = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        limit: PAGE_SIZE.toString(),
        offset: ((page - 1) * PAGE_SIZE).toString()
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      const res = await apiFetch(`/api/email-marketing/audiences/${id}/contacts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudieron cargar contactos');
      setContacts(data.items || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (email, status) => {
    if (!email) return;
    setStatusUpdating(email.toLowerCase());
    try {
      const res = await apiFetch('/api/email-marketing/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, status, reason: 'manual' })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar estado');
      fetchContacts();
    } catch (err) {
      alert(err.message);
    } finally {
      setStatusUpdating('');
    }
  };

  const openEdit = (contact) => {
    if (contact.tipo !== 'manual') {
      return alert('Solo contactos manuales se pueden editar');
    }
    setEditModal({ open: true, contact, name: contact.nombre || contact.name || '', phone: contact.phone || '' });
  };

  const saveEdit = async () => {
    if (!editModal.contact) return;
    try {
      const res = await apiFetch(`/api/email-marketing/audiences/${id}/contacts/${editModal.contact.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: editModal.name, phone: editModal.phone })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo editar');
      setEditModal({ open: false, contact: null, name: '', phone: '' });
      fetchContacts();
    } catch (err) {
      alert(err.message);
    }
  };

  const openImportModal = () => {
    setImportModal({
      open: true,
      uploading: false,
      importing: false,
      error: '',
      fileId: '',
      fileName: '',
      columns: [],
      preview: [],
      totalRows: 0,
      mapping: { email: '', name: '', phone: '' }
    });
  };

  const syncElasticNow = async () => {
    setSyncingElastic(true);
    setSyncMessage('');
    try {
      const res = await apiFetch('/api/email-marketing/elastic/sync', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo sincronizar');
      setSyncMessage(`Sincronización OK (${data.synced || 0} contactos).`);
      fetchContacts();
    } catch (err) {
      setSyncMessage(err.message);
    } finally {
      setSyncingElastic(false);
    }
  };

  const syncElasticEventsNow = async () => {
    setSyncingElasticEvents(true);
    setSyncMessage('');
    try {
      const res = await apiFetch('/api/email-marketing/elastic/events/sync', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo sincronizar eventos');
      setSyncMessage(`Eventos sincronizados (${data.synced || 0}).`);
      fetchContacts();
    } catch (err) {
      setSyncMessage(err.message);
    } finally {
      setSyncingElasticEvents(false);
    }
  };

  const openEditAudience = () => {
    if (!audience) return;
    setEditAudienceModal({
      open: true,
      name: audience.name || '',
      description: audience.description || ''
    });
  };

  const saveAudienceEdit = async () => {
    if (!editAudienceModal.name.trim()) return alert('Nombre obligatorio');
    try {
      const res = await apiFetch(`/api/email-marketing/audiences/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: editAudienceModal.name.trim(),
          description: editAudienceModal.description
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar');
      setAudience(data);
      setEditAudienceModal({ open: false, name: '', description: '' });
    } catch (err) {
      alert(err.message);
    }
  };

  const closeImportModal = () => {
    setImportModal({
      open: false,
      uploading: false,
      importing: false,
      error: '',
      fileId: '',
      fileName: '',
      columns: [],
      preview: [],
      totalRows: 0,
      mapping: { email: '', name: '', phone: '' }
    });
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    setImportModal((prev) => ({
      ...prev,
      uploading: true,
      error: '',
      fileName: file.name,
      columns: [],
      preview: [],
      totalRows: 0,
      fileId: '',
      mapping: { email: '', name: '', phone: '' }
    }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiFetch(`/api/email-marketing/audiences/${id}/import/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error previsualizando archivo');
      const columns = Array.isArray(data.columns) ? data.columns : [];
      setImportModal((prev) => ({
        ...prev,
        uploading: false,
        fileId: data.fileId || '',
        columns,
        preview: Array.isArray(data.preview) ? data.preview : [],
        totalRows: Number(data.totalRows || 0),
        mapping: {
          email: guessColumn(columns, ['email', 'correo', 'e-mail']),
          name: guessColumn(columns, ['nombre', 'name', 'razon', 'contacto']),
          phone: guessColumn(columns, ['telefono', 'tel', 'phone', 'movil', 'mobile', 'whatsapp'])
        }
      }));
    } catch (err) {
      setImportModal((prev) => ({ ...prev, uploading: false, error: err.message }));
    }
  };

  const submitImport = async () => {
    if (!importModal.fileId) return;
    if (!importModal.mapping.email) {
      setImportModal((prev) => ({ ...prev, error: 'Selecciona la columna de email.' }));
      return;
    }
    setImportModal((prev) => ({ ...prev, importing: true, error: '' }));
    try {
      const res = await apiFetch(`/api/email-marketing/audiences/${id}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          fileId: importModal.fileId,
          mapping: importModal.mapping
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo importar');
      alert(`Importación completada. Nuevos: ${data.imported || 0}, actualizados: ${data.updated || 0}, omitidos: ${data.skipped || 0}`);
      closeImportModal();
      fetchContacts();
    } catch (err) {
      setImportModal((prev) => ({ ...prev, importing: false, error: err.message }));
    }
  };

  useEffect(() => {
    setPage(1);
    setAudience(location.state?.audience || null);
    fetchAudience();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchContacts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, page, filters]);

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  return (
    <div className="container py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <div className="text-muted small">Audiencia</div>
          <h3 className="mb-0">{audience?.name || 'Audiencia'}</h3>
          {audience?.description && <div className="text-muted small">{audience.description}</div>}
        </div>
        <div className="d-flex gap-2">
          {isManualAudience() && (
            <button className="btn btn-primary" onClick={openImportModal}>Importar</button>
          )}
          <button className="btn btn-outline-primary" onClick={openEditAudience}>Editar audiencia</button>
          <button className="btn btn-outline-primary" onClick={syncElasticNow} disabled={syncingElastic}>
            {syncingElastic ? 'Sincronizando...' : 'Sincronizar Elastic'}
          </button>
          <button className="btn btn-outline-secondary" onClick={syncElasticEventsNow} disabled={syncingElasticEvents}>
            {syncingElasticEvents ? 'Sincronizando eventos...' : 'Sync opens/clicks'}
          </button>
          <Link className="btn btn-outline-secondary" to="/email-marketing">Volver</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {syncMessage && (
        <div className={`alert ${syncMessage.startsWith('Sincronización OK') ? 'alert-success' : 'alert-warning'}`}>
          {syncMessage}
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row g-2 align-items-end mb-3">
            <div className="col-md-4">
              <label className="form-label">Buscar</label>
              <input
                className="form-control"
                placeholder="Nombre o email"
                value={filters.search}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, search: e.target.value })); }}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Estado</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, status: e.target.value })); }}
              >
                <option value="">Todos</option>
                <option value="subscribed">Suscrito</option>
                <option value="unsubscribed">Dado de baja</option>
                <option value="bounced">Rebotado</option>
                <option value="abuse">Abuse</option>
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button className="btn btn-outline-secondary w-100" onClick={() => { setFilters({ search: '', status: '' }); setPage(1); }}>
                Limpiar
              </button>
            </div>
            <div className="col-md-3 d-flex align-items-end justify-content-end">
              <button className="btn btn-sm btn-outline-secondary" onClick={fetchContacts} disabled={loading}>
                {loading ? 'Actualizando...' : 'Refrescar'}
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Nombre</th>
                  <th>Teléfono</th>
                  <th>Origen</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {contacts.length ? contacts.map((c, idx) => {
                  const status = c.email_status || (c.unsubscribed ? 'unsubscribed' : 'subscribed');
                  const badgeClass = status === 'bounced'
                    ? 'bg-warning text-dark'
                    : status === 'abuse'
                      ? 'bg-dark'
                      : status === 'unsubscribed'
                        ? 'bg-danger'
                        : 'bg-success';
                  const statusLabel = status === 'bounced'
                    ? 'Rebotado'
                    : status === 'abuse'
                      ? 'Abuse'
                      : status === 'unsubscribed'
                        ? 'Dado de baja'
                        : 'Suscrito';
                  return (
                  <tr key={`${c.email}-${idx}`}>
                    <td>{c.email}</td>
                    <td>{c.nombre || c.name || '-'}</td>
                    <td>{c.phone || '-'}</td>
                    <td className="text-capitalize">{c.tipo || '-'}</td>
                    <td>
                      <span className={`badge ${badgeClass}`}>{statusLabel}</span>
                    </td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm">
                        <select
                          className="form-select form-select-sm"
                          value={status}
                          onChange={(e) => updateContactStatus(c.email, e.target.value)}
                          disabled={statusUpdating === (c.email || '').toLowerCase()}
                        >
                          <option value="subscribed">Suscrito</option>
                          <option value="unsubscribed">Dado de baja</option>
                          <option value="bounced">Rebotado</option>
                          <option value="abuse">Abuse</option>
                        </select>
                        {c.tipo === 'manual' && (
                          <>
                            <button className="btn btn-outline-primary" onClick={() => openEdit(c)}>Editar</button>
                            <button
                              className="btn btn-outline-danger"
                              onClick={async () => {
                                if (!window.confirm(`¿Eliminar ${c.email} de esta audiencia?`)) return;
                                try {
                                  const res = await apiFetch(`/api/email-marketing/audiences/${id}/contacts/${c.id}`, {
                                    method: 'DELETE',
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  if (res.status === 401) return handleUnauthorized();
                                  if (!res.ok) throw new Error('No se pudo eliminar');
                                  fetchContacts();
                                } catch (err) {
                                  alert(err.message);
                                }
                              }}
                            >
                              Eliminar
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
                }) : (
                  <tr><td colSpan="6" className="text-muted">Sin contactos.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted small">Total: {total}</div>
            <div className="d-flex align-items-center gap-2">
              <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</button>
              <span className="small">Página {page} / {totalPages}</span>
              <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Siguiente</button>
            </div>
          </div>
        </div>
      </div>

      {editAudienceModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Editar audiencia</h5>
                <button
                  className="btn-close"
                  onClick={() => setEditAudienceModal({ open: false, name: '', description: '' })}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Nombre</label>
                  <input
                    className="form-control"
                    value={editAudienceModal.name}
                    onChange={(e) => setEditAudienceModal((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Descripción</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={editAudienceModal.description}
                    onChange={(e) => setEditAudienceModal((p) => ({ ...p, description: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setEditAudienceModal({ open: false, name: '', description: '' })}>
                  Cancelar
                </button>
                <button className="btn btn-primary" onClick={saveAudienceEdit}>Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Editar contacto</h5>
                <button className="btn-close" onClick={() => setEditModal({ open: false, contact: null, name: '', phone: '' })}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Nombre</label>
                  <input className="form-control" value={editModal.name} onChange={(e) => setEditModal((p) => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Teléfono</label>
                  <input className="form-control" value={editModal.phone} onChange={(e) => setEditModal((p) => ({ ...p, phone: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setEditModal({ open: false, contact: null, name: '', phone: '' })}>Cancelar</button>
                <button className="btn btn-primary" onClick={saveEdit}>Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {importModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Importar contactos</h5>
                <button className="btn-close" onClick={closeImportModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Archivo CSV o Excel</label>
                  <input
                    className="form-control"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => handleImportFile(e.target.files?.[0])}
                    disabled={importModal.uploading}
                  />
                  {importModal.fileName && (
                    <div className="text-muted small mt-1">{importModal.fileName}</div>
                  )}
                </div>

                {importModal.uploading && <div className="text-muted small">Procesando archivo...</div>}

                {!!importModal.columns.length && (
                  <>
                    <div className="mb-3">
                      <div className="fw-semibold mb-2">Mapeo de campos</div>
                      <div className="row g-2">
                        <div className="col-md-4">
                          <label className="form-label small">Email *</label>
                          <select
                            className="form-select"
                            value={importModal.mapping.email}
                            onChange={(e) => setImportModal((prev) => ({
                              ...prev,
                              mapping: { ...prev.mapping, email: e.target.value }
                            }))}
                          >
                            <option value="">Selecciona columna</option>
                            {importModal.columns.map((col) => (
                              <option key={col} value={col}>{col}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small">Nombre</label>
                          <select
                            className="form-select"
                            value={importModal.mapping.name}
                            onChange={(e) => setImportModal((prev) => ({
                              ...prev,
                              mapping: { ...prev.mapping, name: e.target.value }
                            }))}
                          >
                            <option value="">Sin asignar</option>
                            {importModal.columns.map((col) => (
                              <option key={col} value={col}>{col}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small">Teléfono</label>
                          <select
                            className="form-select"
                            value={importModal.mapping.phone}
                            onChange={(e) => setImportModal((prev) => ({
                              ...prev,
                              mapping: { ...prev.mapping, phone: e.target.value }
                            }))}
                          >
                            <option value="">Sin asignar</option>
                            {importModal.columns.map((col) => (
                              <option key={col} value={col}>{col}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="text-muted small mt-2">Filas detectadas: {importModal.totalRows}</div>
                    </div>

                    {!!importModal.preview.length && (
                      <div>
                        <div className="fw-semibold mb-2">Vista previa</div>
                        <div className="table-responsive">
                          <table className="table table-sm table-striped">
                            <thead>
                              <tr>
                                {importModal.columns.map((col) => (
                                  <th key={col}>{col}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {importModal.preview.map((row, idx) => (
                                <tr key={idx}>
                                  {importModal.columns.map((col) => (
                                    <td key={`${idx}-${col}`}>{row[col] || '-'}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {importModal.error && <div className="alert alert-danger mt-3">{importModal.error}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={closeImportModal}>Cancelar</button>
                <button
                  className="btn btn-primary"
                  disabled={!importModal.fileId || importModal.importing}
                  onClick={submitImport}
                >
                  {importModal.importing ? 'Importando...' : 'Importar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudienceContactsPage;
